import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PlusIcon, TrashIcon, UserPlusIcon, PencilSimpleIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { createUser, deleteUser, listUsers, updateUser } from "@/lib/api";

const ROLES = [
  { value: "admin", label: "Admin — full access, delete, CSV, audit" },
  { value: "editor", label: "Editor — create/update ingredients & formulations" },
  { value: "viewer", label: "Viewer — read-only + exports" },
];

function UserDialog({ trigger, initial, onSaved }) {
  const editing = !!initial;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "viewer", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { email: initial.email, name: initial.name || "", role: initial.role, password: "" } : { email: "", name: "", role: "viewer", password: "" });
  }, [open, initial]);

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await updateUser(initial.id, payload);
        toast.success("User updated");
      } else {
        if (!form.email || form.password.length < 8) {
          toast.error("Email required and password must be ≥ 8 chars"); setBusy(false); return;
        }
        await createUser(form);
        toast.success("User created");
      }
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-none max-w-md" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{editing ? "Edit user" : "Invite user"}</DialogTitle>
          <DialogDescription>{editing ? "Update role, name, or password." : "Add a new team member with a password."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-kicker">Email</Label>
            <Input value={form.email} disabled={editing} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none h-9 font-mono text-sm mt-1" data-testid="user-email" />
          </div>
          <div>
            <Label className="label-kicker">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none h-9 font-mono text-sm mt-1" data-testid="user-name" />
          </div>
          <div>
            <Label className="label-kicker">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="rounded-none h-9 font-mono text-sm mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-kicker">{editing ? "New password (optional)" : "Password"}</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="≥ 8 characters" className="rounded-none h-9 font-mono text-sm mt-1" data-testid="user-password" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="btn-primary rounded-none" data-testid="user-save">{busy ? "Saving…" : (editing ? "Save" : "Create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    listUsers().then(setItems).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleDelete = async (u) => {
    try {
      await deleteUser(u.id);
      toast.success(`${u.email} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <main className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 fade-in" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="label-kicker">Admin</div>
          <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tighter mt-2">Team & Roles</h1>
        </div>
        <UserDialog
          trigger={
            <Button className="btn-primary rounded-none h-9 font-mono text-[11px] uppercase tracking-[0.18em]" data-testid="invite-user-btn">
              <UserPlusIcon size={14} className="mr-2" /> Invite User
            </Button>
          }
          onSaved={load}
        />
      </div>

      <section className="border border-[var(--border)] bg-white">
        {loading ? (
          <div className="p-10 text-center font-mono text-xs text-[var(--text-muted)]">Loading…</div>
        ) : (
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr>
                {["EMAIL", "NAME", "ROLE", "CREATED", ""].map((h, i) => (
                  <th key={i} className="label-kicker text-left px-4 py-3 border-b border-[var(--border)] bg-[var(--surface)]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="cell-border" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{u.email}{me?.id === u.id && <span className="ml-2 text-[var(--primary)]">(you)</span>}</td>
                  <td className="px-4 py-3">{u.name || "—"}</td>
                  <td className="px-4 py-3"><span className="chip uppercase">{u.role}</span></td>
                  <td className="px-4 py-3 font-mono text-[10px] text-[var(--text-muted)]">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <UserDialog
                        initial={u}
                        trigger={
                          <button className="text-[var(--text-muted)] hover:text-[var(--primary)]" data-testid={`user-edit-${u.id}`}>
                            <PencilSimpleIcon size={14} />
                          </button>
                        }
                        onSaved={load}
                      />
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="text-[var(--text-muted)] hover:text-[var(--danger)] disabled:opacity-30" disabled={me?.id === u.id} data-testid={`user-delete-${u.id}`}>
                            <TrashIcon size={14} />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-none">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {u.email}?</AlertDialogTitle>
                            <AlertDialogDescription>They will lose access immediately. This cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(u)} className="rounded-none bg-[var(--danger)] hover:bg-[var(--danger)]/90">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}
